import { Component, effect, inject, OnDestroy, signal } from '@angular/core'
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from "@angular/router"
import { LoadingOverlay } from "@features/loading-overlay/loading-overlay.service"
import { filter, from, map, of, Subscription, switchMap, take, tap } from "rxjs"
import { takeUntilDestroyed } from "@angular/core/rxjs-interop"
import { FlowRouteStateStore } from "@features/client-flow/flow-wrapper/flow-state"
import { APP_ROUTE_NAMES } from "@app/app.routes"


@Component({
    selector: 'app-flow-wrapper',
    imports: [RouterOutlet],
    template: `
    @if (!initialNavigation()) {
      <router-outlet />
    }
  `
})
export class FlowWrapperComponent implements OnDestroy {
  loading = signal(true)

  // Direct navigation usually happens only during the flow; in that case, page preload is nice since most likely this is the target one.
  // For initial navigation from the browser, we don't want to do that
  initialNavigation = signal(true)

  #currentUrl = ''

  #overlay = inject(LoadingOverlay)

  #routeStore = inject(FlowRouteStateStore)

  #sub: Subscription

  #showLoadingOverlay = effect(() => {
    if (this.loading()) {
      this.#overlay.show()
    } else {
      this.#overlay.hide()
    }
  })

  constructor(router: Router) {
    this.#sub = router.events.pipe(
      takeUntilDestroyed(),
      filter((event): event is NavigationStart | NavigationEnd => event instanceof NavigationStart || event instanceof NavigationEnd),
      filter(({ url }) => url !== this.#currentUrl && url.startsWith(`/${APP_ROUTE_NAMES.FLOW}`)),
      tap(event => {
        if (event instanceof NavigationStart) {
          this.loading.set(true)
        }
      }),
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      switchMap(event => {
        const previous = this.#currentUrl
        this.#currentUrl = event.urlAfterRedirects

        return this.#routeStore
          .get(previous)
          .pipe(
            take(1),
            map(navigation => ({ navigation, sameNavigation: navigation.every(part => event.urlAfterRedirects.includes(part)) }))
          )
      }),
      switchMap(({ navigation, sameNavigation }) => {
        // We are already on this url, avoid extra navigation and infinite redirect loops
        if (sameNavigation) {
          return of(undefined)
        }

        this.#currentUrl = router.createUrlTree(navigation, { queryParamsHandling: 'preserve' }).toString()

        return from(router.navigateByUrl(this.#currentUrl, { replaceUrl: true }))
      })
    ).subscribe(() => {
      // Resolve loader only when we are certain that navigation finished
      this.loading.set(false)
      this.initialNavigation.set(false)
    })
  }

  ngOnDestroy() {
    this.#sub.unsubscribe()
    this.#overlay.hide()
  }
}
